import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input, Modal, Text } from '@/lib/v2/components';
import { Divide, Flex } from '@/lib/v2/components/Layout/Stack';

import ButtonsSection from './ButtonsSection';

type FormInput = {
  name: string;
};

interface ModalCreateItemProps {
  id?: string;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSubmit?: (nameValue: string) => void;
  isLoading?: boolean;
  inputLabel?: string;
  textAction?: string;
  textClose?: string;
  rules?: object;
  /** controller value input */
  value?: string;
}

const ModalSingleInput = ({
  id,
  isOpen,
  onClose,
  title,
  onSubmit,
  inputLabel,
  textAction,
  textClose,
  isLoading,
  rules,
  value,
}: ModalCreateItemProps) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormInput>();

  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState('');

  const RULES = {
    name: rules ?? {
      required: t('CREATE_SEGMENT_MODAL.emptyField'),
    },
  };

  useEffect(() => {
    if (!isOpen && !value) setInputValue('');
  }, [isOpen, value]);

  useEffect(() => {
    if (value) setInputValue(value);
  }, [value]);

  const onAction = handleSubmit((values) => {
    const { name } = values;
    onSubmit?.(name);
  });

  const handleChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const label = inputLabel ?? t('CREATE_SEGMENT_MODAL.fieldName');

  return (
    <Modal noPadding withAuto id={id} open={isOpen} onClose={handleClose}>
      <div className="h-full p-6">
        <Divide>
          <Flex column gapSize="small" itemAlignment="stretch">
            <Text color="light-black" fontWeight="bold">
              {title}
            </Text>
            <div className="h-[86%]">
              <div className="w-[30vw] p-[1%] py-4">
                <div className="w-full">
                  <section className="mb-3">
                    <div className="pt-4">
                      <Input
                        isRequired
                        control={control}
                        disabled={isLoading}
                        error={errors?.name && true}
                        id={`${label?.toLowerCase() ?? 'name'}-input-id`}
                        label={label}
                        message={errors?.name?.message}
                        name={label?.toLowerCase()}
                        rules={RULES.name}
                        value={inputValue}
                        onChange={handleChangeInput}
                      />
                    </div>
                  </section>
                </div>
              </div>
            </div>
            <ButtonsSection
              isLoading={isLoading}
              textAction={textAction}
              textClose={textClose}
              onAction={onAction}
              onClose={handleClose}
            />
          </Flex>
        </Divide>
      </div>
    </Modal>
  );
};

export default memo(ModalSingleInput);
